// Physical page-key handling, shared by the reader and the library.
//
// Moved out of ReaderView 2026-10-06 when the library gained pagination:
// the Onyx quirks below must be encoded in exactly ONE place, or the two
// screens will drift and the hardware buttons will work on one and not the
// other. ReaderView binds this inside the reader (document capture, window,
// and the epub.js iframe); LibraryView binds it on the library.

// Map a physical page key (PageUp/PageDown = BOOX upper/lower buttons) to a
// turn direction from the user's chosen order. This is the SINGLE place the
// reversal lives, so BOOX's own config and our code can't reverse twice.
//   kindle:   upper(PageUp)→next,  lower(PageDown)→prev   (Kindle default)
//   reversed: upper(PageUp)→prev,  lower(PageDown)→next   (browser standard)
export function pageKeyDirection(
  key: string,
  order: "kindle" | "reversed" | undefined,
): "next" | "prev" | null {
  const upper = key === "PageUp";
  const lower = key === "PageDown";
  if (!upper && !lower) return null;
  const kindle = order === "kindle";
  if (upper) return kindle ? "next" : "prev";
  return kindle ? "prev" : "next";
}

// 2026-10-06 — BOOX hardware page-turn buttons ("the Amazon flip buttons").
//
// pageKeyDirection() above only looks at `event.key`, and only at the two
// literal strings "PageUp" / "PageDown". That is too narrow for an Onyx
// device: depending on model, firmware and whether the page is running in
// Chrome or in the installed-PWA WebView, a side button can arrive as
//   - key "PageUp"/"PageDown"                      (the happy path)
//   - key "" or "Unidentified" with code "PageUp"  (WebView, no key mapping)
//   - key "" and code "" with keyCode 33/34        (older WebView)
//   - an arrow key, if the user has remapped the buttons in Onyx settings
// so we check all three fields and accept arrows as well. Matching on a
// field the device didn't populate is harmless; missing the one it did
// populate means the buttons do nothing, which is where we are today.
//
// This is the SINGLE decision point for "should this key turn a page",
// shared by the outer-document handler and the in-iframe handler, so the
// two can't drift apart (they already had: the iframe copy ignored the
// button-order setting for arrows and called rendition.next() directly
// while the outer one went through slidePageTurn()).
//
// NOT handled here, deliberately:
//   - Space / Shift+Space. The brief asked for them, but Space is already
//     play/pause for read-aloud, which is the point of this app, and no
//     BOOX hardware button emits Space. Rebinding it would cost a working
//     shortcut to buy nothing on the target device. Say the word and it's
//     a two-line change.
//   - Volume keys. The web platform cannot see them on Android at all;
//     that needs the wrapper-APK path the key-test screen mentions.
export function pageTurnFromKeyEvent(
  e: KeyboardEvent,
  order: "kindle" | "reversed" | undefined,
): "next" | "prev" | null {
  // Never steal a key from a text field — search, note editors, inputs.
  const t = e.target as HTMLElement | null;
  if (
    t &&
    (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)
  ) {
    return null;
  }
  // A modified keypress is a browser/OS shortcut, not a page turn.
  if (e.ctrlKey || e.metaKey || e.altKey) return null;

  const key = e.key || "";
  const code = e.code || "";
  const kc = e.keyCode || 0;

  const isPageUp = key === "PageUp" || code === "PageUp" || kc === 33;
  const isPageDown = key === "PageDown" || code === "PageDown" || kc === 34;
  if (isPageUp || isPageDown) {
    // Reuse the one place the Kindle/reversed flip lives.
    return pageKeyDirection(isPageUp ? "PageUp" : "PageDown", order);
  }

  // Arrows are fixed-direction: right/down = forward, left/up = back.
  // These do NOT honour buttonOrder — that setting describes the physical
  // upper/lower button pair, and reversing arrows would just be wrong.
  // (Swipe follows the same rule; see SWIPE_* below.)
  if (key === "ArrowRight" || code === "ArrowRight" || kc === 39) return "next";
  if (key === "ArrowDown" || code === "ArrowDown" || kc === 40) return "next";
  if (key === "ArrowLeft" || code === "ArrowLeft" || kc === 37) return "prev";
  if (key === "ArrowUp" || code === "ArrowUp" || kc === 38) return "prev";

  return null;
}

